import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Rasenpflege",
  "Heckenschnitt",
  "Baumpflege",
  "Pflanzenpflege",
  "Hausmeisterservice",
  "Entrümpelung",
  "Individueller Wunsch",
];

const EMPTY = { name: "", email: "", phone: "", services: [], message: "" };
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter((x) => x !== s)
        : [...f.services, s],
    }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Bitte geben Sie Ihren Namen ein.";
    const em = form.email.trim();
    const ph = form.phone.trim();
    if (!em && !ph) {
      const msg = "Bitte E-Mail oder Telefon angeben — mindestens eines.";
      err.email = msg;
      err.phone = msg;
    } else if (em && !isEmail(em)) {
      err.email = "Bitte eine gültige E-Mail-Adresse eingeben.";
    }
    if (!form.message.trim()) err.message = "Bitte beschreiben Sie Ihr Anliegen.";
    return err;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length) {
      setErrors(err);
      toast.error("Bitte füllen Sie die markierten Felder aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        service: form.services.length ? form.services.join(", ") : null,
        message: form.message.trim(),
      });
      setDone(true);
      setForm(EMPTY);
      setErrors({});
      toast.success("Danke! Wir melden uns schnellstmöglich bei Ihnen.");
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32"
    >
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-yx-secondary mb-5">
            Kontakt
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-bold tracking-tighter text-yx-primary text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
          >
            Lassen Sie uns über Ihren Garten sprechen.
          </motion.h2>
          <p className="mt-6 text-yx-muted leading-relaxed max-w-md">
            Füllen Sie das Formular aus und wir melden uns zeitnah bei Ihnen.
            Kein Anliegen ist zu klein.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div>
              <p className="text-yx-muted mb-1">E-Mail</p>
              <a
                href="mailto:info.yardtf@gmail.com"
                data-testid="contact-email-link"
                className="font-display text-xl text-yx-primary hover:text-yx-secondary transition-colors"
              >
                info.yardtf@gmail.com
              </a>
            </div>
            <div>
              <p className="text-yx-muted mb-1">Telefon</p>
              <a
                href="tel:+4915229816526"
                data-testid="contact-phone-link"
                className="font-display text-xl text-yx-primary hover:text-yx-secondary transition-colors"
              >
                +49 (0) 1522 9816526
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="contact-success"
              className="flex flex-col items-start gap-4 rounded-sm border border-yx-border bg-yx-surface p-10"
            >
              <CheckCircle2 className="text-yx-secondary" size={40} />
              <h3 className="font-display text-3xl font-bold text-yx-primary">
                Vielen Dank!
              </h3>
              <p className="text-yx-muted max-w-md">
                Ihre Anfrage ist bei uns eingegangen. Wir melden uns
                schnellstmöglich bei Ihnen.
              </p>
              <button
                data-testid="contact-reset"
                onClick={() => setDone(false)}
                className="mt-2 rounded-full border border-yx-border px-6 py-3 text-sm font-semibold text-yx-primary hover:border-yx-primary transition-colors"
              >
                Weitere Anfrage
              </button>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Max Mustermann"
                  className={`bg-white ${errors.name ? "border-destructive" : ""}`}
                />
                {errors.name && (
                  <p data-testid="error-name" className="text-xs text-destructive">
                    {errors.name}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="max@beispiel.de"
                  className={`bg-white ${errors.email ? "border-destructive" : ""}`}
                />
                {errors.email && (
                  <p data-testid="error-email" className="text-xs text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="0170 1234567"
                  className={`bg-white ${errors.phone ? "border-destructive" : ""}`}
                />
                {errors.phone && (
                  <p data-testid="error-phone" className="text-xs text-destructive">
                    {errors.phone}
                  </p>
                )}
              </div>
              <p className="sm:col-span-2 -mt-2 text-xs text-yx-muted">
                * Mindestens eines der beiden Felder E-Mail oder Telefon ausfüllen.
              </p>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label>Leistung(en) — Mehrfachauswahl möglich</Label>
                <div
                  role="group"
                  data-testid="contact-services"
                  className="flex flex-wrap gap-2"
                >
                  {SERVICES.map((s) => {
                    const active = form.services.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => toggleService(s)}
                        data-testid={`service-chip-${s}`}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                          active
                            ? "border-yx-primary bg-yx-primary text-white"
                            : "border-yx-border bg-white text-yx-primary hover:border-yx-primary"
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`inline-block h-3.5 w-3.5 rounded-[3px] border ${
                            active
                              ? "border-white bg-white"
                              : "border-yx-border"
                          } flex items-center justify-center`}
                        >
                          {active && (
                            <svg
                              viewBox="0 0 20 20"
                              className="h-3 w-3 text-yx-primary"
                              fill="currentColor"
                            >
                              <path d="M7.5 13.5l-3-3 1.06-1.06L7.5 11.38l6.94-6.94L15.5 5.5z" />
                            </svg>
                          )}
                        </span>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Label htmlFor="message">Nachricht *</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={5}
                  placeholder="Beschreiben Sie kurz, was Ihr Garten braucht…"
                  className={`bg-white resize-none ${errors.message ? "border-destructive" : ""}`}
                />
                {errors.message && (
                  <p data-testid="error-message" className="text-xs text-destructive">
                    {errors.message}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-yx-primary px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-yx-secondary hover:shadow-xl hover:shadow-yx-primary/20 disabled:opacity-60"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Wird gesendet…" : "Anfrage absenden"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
